// src/hooks/useDayCloseSection.ts
/**
 * DayClose Section 的業務邏輯 Hook
 *
 * 從 DayCloseSection 抽取，讓 Today.tsx 可持有 modal 狀態，
 * 實現多個入口（DayCloseSection banner + Evening 空狀態 CTA）共用同一個 modal。
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDayCloseAPI } from './useDayCloseAPI';
import { useDebugMode } from './useDebugMode';
import { useCurrentTime } from './useCurrentTime';
import { DayClose } from '../types/dayClose';
import { logger } from '../utils/logger';

export interface UseDayCloseSectionReturn {
  // 時間窗口
  isEveningTime: boolean;
  // DayClose 資料
  dayClose: DayClose | null;
  isLoading: boolean;
  error: string | null;
  // Modal 狀態（由 Today.tsx 持有，此處為 open/close 動作）
  isModalOpen: boolean;
  handleOpen: () => Promise<void>;
  handleClose: () => void;
  handleRetry: () => void;
}

export function useDayCloseSection(): UseDayCloseSectionReturn {
  const { currentUser } = useAuth();
  const { checkTodayDayClose, createDayClose, loadDayClose } = useDayCloseAPI();
  const { isDebugMode } = useDebugMode();
  const currentTime = useCurrentTime();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dayClose, setDayClose] = useState<DayClose | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEveningTime = useMemo(() => {
    const hour = currentTime.getHours();
    return isDebugMode ? true : (hour >= 19 && hour < 24);
  }, [isDebugMode, currentTime]);

  const loadDayCloseStatus = useCallback(async () => {
    if (!currentUser) return;
    setIsLoading(true);
    try {
      setError(null);
      const result = await checkTodayDayClose(currentUser.uid);
      setDayClose(result);
    } catch (err) {
      logger.error('useDayCloseSection', 'Failed to check Day Close status', err);
      setError('Failed to check Day Close status');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, checkTodayDayClose]);

  useEffect(() => {
    if (currentUser && isEveningTime) {
      loadDayCloseStatus();
    }
  }, [currentUser, isEveningTime, loadDayCloseStatus]);

  const handleOpen = useCallback(async () => {
    if (!currentUser || isLoading) return;
    setIsLoading(true);
    setError(null);
    try {
      const dayCloseId = await createDayClose(currentUser.uid);
      if (!dayCloseId) throw new Error('Failed to get or create a Day Close session.');
      const freshData = await loadDayClose(dayCloseId);
      if (!freshData) throw new Error('Could not load the Day Close session data after creation.');
      setDayClose(freshData);
      setIsModalOpen(true);
    } catch (err) {
      logger.error('useDayCloseSection', 'Day Close process failed', err);
      setError('Failed to process Day Close request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, isLoading, createDayClose, loadDayClose]);

  const handleClose = useCallback(() => {
    setIsModalOpen(false);
    loadDayCloseStatus();
  }, [loadDayCloseStatus]);

  const handleRetry = useCallback(() => {
    setError(null);
    loadDayCloseStatus();
  }, [loadDayCloseStatus]);

  return {
    isEveningTime,
    dayClose,
    isLoading,
    error,
    isModalOpen,
    handleOpen,
    handleClose,
    handleRetry,
  };
}
